import DayGridAllDayComponent from './DayGridAllDayComponent';
import DayGridAllDayHeaderComponent from './DayGridAllDayHeaderComponent';
import DayGridContextProvider from './DayGridContextProvider';
import DayGridInDayComponent from './DayGridInDayComponent';
import DayGridInDayContentComponent from './DayGridInDayContentComponent';
import SurfaceGridHandlerAdapter, { GridClickParams } from '../utils/SurfaceGridHandlerAdapter';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { getCharm } from 'owa-calendar-events-store';
import type { CalendarDayContent } from 'owa-calendar-surface-core';
import { isDragItemId } from 'owa-calendar-surface-dategrid-common-dnd';
import { CalendarEvent, renderAsAllDay } from 'owa-calendar-types';
import type { ClientItemId } from 'owa-client-ids';
import { getLightColorScheme } from 'owa-color-utils/lib/colorUtils';
import type { OwaDate } from 'owa-datetime';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isPreviewItemId } from 'owa-quick-compose-controller';
import React from 'react';
import SurfaceEventHandlerAdapter, {
    SurfaceMouseEvent,
    SurfaceKeyboardEvent,
} from '../utils/SurfaceEventHandlerAdapter';
import {
    createItemsDecorator,
    inDayItemTemplate,
    allDayItemTemplate,
    isOverflowIndicatorId,
    overflowItemTemplate,
    CalendarSurfaceItemsConfig,
    updateAllDayWellExpanded,
    blankItemTemplate as blankItemGridTemplate,
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    createDragHelperDecorator,
    freeBusyTemplate,
} from 'owa-calendar-surface-dategrid';
import {
    CalendarEventInteraction,
    CalendarEventInteractionCallback,
} from 'owa-calendar-event-interaction-schema';
import {
    canDragItem,
    canDropItemType,
    getSurfaceItemForPreview,
    onDragEnd,
    onDragStart,
    onItemDropped,
} from 'owa-calendar-surface-dategrid-common-dnd-callbacks';
import { DraggableDayGrid, createDayGridConfig } from 'owa-draggable-day-grid';
import { getScenarioStore, shouldGrayOutItem, DayGridViewState } from 'owa-day-grid-core-schema';
import type { PerformanceDatapoint } from 'owa-analytics';

const styles = require('./DayGridView.scss');
const classNames = require('classnames');

export interface DayGridViewProps {
    scenario: string;
    /** Enables gray shading of non-working hours and past days
     *  and auto-scrolling to show those hours -- defaults to true */
    enableWorkingHours?: boolean;
    selectedEventId?: ClientItemId;
    maxAllDayRowsToShow?: number;
    highlightedEventId?: string;
    calendarEventInteractionCallback?: CalendarEventInteractionCallback;
    shouldSortAllDaySelectionToFront?: boolean;
    onGridClick?: (params: GridClickParams & { viewState: DayGridViewState }) => void;
    onGridDoubleClick?: (params: GridClickParams & { viewState: DayGridViewState }) => void;
    onGridMouseDown?: (ev: React.MouseEvent<HTMLElement>) => void;
    handleEditOnItemDrop?: (event: Partial<CalendarEvent>, eventHasUpdates: boolean) => void;
    numDaysInDayView?: number | undefined;
    greyOutUnselectedItems?: boolean;
    shouldHighlightColumnAllDayBusyAway?: boolean;
    perfDatapoint?: PerformanceDatapoint;
}

const DayGridView = observer(function DayGridView(props: DayGridViewProps) {
    const {
        scenario,
        enableWorkingHours,
        maxAllDayRowsToShow,
        highlightedEventId,
        shouldSortAllDaySelectionToFront,
        calendarEventInteractionCallback,
        onGridClick,
        onGridDoubleClick,
        onGridMouseDown,
        handleEditOnItemDrop,
        numDaysInDayView,
        greyOutUnselectedItems = true,
        shouldHighlightColumnAllDayBusyAway = true,
        perfDatapoint,
    } = props;
    const store = getScenarioStore(scenario);

    const onClick = (
        id: ClientItemId,
        parentFolderId: string,
        event: SurfaceMouseEvent | SurfaceKeyboardEvent
    ) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.Click,
            id: id,
            target: event.currentTarget,
        });
    };
    const onClickHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onClick),
        []
    );

    const onDoubleClick = (id: ClientItemId, parentFolderId: string, event: SurfaceMouseEvent) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.DoubleClick,
            id: id,
            target: event.currentTarget,
        });
    };
    const onDoubleClickHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onDoubleClick),
        []
    );

    const onContextMenu = (id: ClientItemId, parentFolderId: string, event: SurfaceMouseEvent) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.RightClick,
            id: id,
            parentFolderId: parentFolderId,
            evt: event,
        });
    };
    const onContextMenuHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onContextMenu, true),
        []
    );
    const onMouseDownHandler = React.useCallback(
        (evt: React.MouseEvent<HTMLDivElement>) => {
            // This is needed to prevent the column from getting the mouse down events.
            if (!store?.enableDragDrop) {
                evt.stopPropagation();
            }
        },
        [store?.enableDragDrop]
    );

    const onSurfaceGridClick = (scenario: string, params: GridClickParams) => {
        onGridClick?.({ ...params, viewState: getScenarioStore(scenario) });
    };
    const onSurfaceGridClickHandler = React.useCallback(
        SurfaceGridHandlerAdapter(scenario, onSurfaceGridClick),
        [scenario]
    );

    const onSurfaceGridDoubleClick = (scenario: string, params: GridClickParams) => {
        onGridDoubleClick?.({
            ...params,
            viewState: getScenarioStore(scenario),
        });
    };
    const onSurfaceGridDoubleClickHandler = React.useCallback(
        SurfaceGridHandlerAdapter(scenario, onSurfaceGridDoubleClick),
        [scenario]
    );

    const onDeleteKey = (id: ClientItemId, parentFolderId: string, event: SurfaceKeyboardEvent) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.DeleteKey,
            id: id,
            target: event.currentTarget,
        });
    };
    const onKeydownHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceKeyboardEvent>(
            (id, parentFolderId, event) => {
                switch (event.keyCode) {
                    case KeyCodes.enter:
                        onClick(id, parentFolderId, event);
                        break;
                    case KeyCodes.del:
                    case KeyCodes.backspace:
                        onDeleteKey(id, parentFolderId, event);
                        break;
                }
            },
            false,
            ({ keyCode }) => {
                return (
                    keyCode == KeyCodes.del ||
                    keyCode == KeyCodes.backspace ||
                    keyCode == KeyCodes.enter
                );
            }
        ),
        []
    );

    const onItemDroppedHandler = React.useCallback(
        (droppedItem: CalendarEvent, originalItem: CalendarEvent, dragInfo: DragData) => {
            onItemDropped(droppedItem, originalItem, dragInfo, handleEditOnItemDrop);
        },
        [handleEditOnItemDrop]
    );

    const shouldGrayOutItemForScenario = React.useCallback(
        (event: CalendarEvent) => shouldGrayOutItem(highlightedEventId, event),
        [highlightedEventId]
    );

    const onOverflowItemClicked = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            ev.stopPropagation();
            if (store) {
                updateAllDayWellExpanded(store, true, 'ExpandButton');
            }
        },
        [store]
    );

    const viewState = store;

    const getCalendarDayContentConfig = React.useCallback(
        (content: CalendarDayContent, calendarConfig: CalendarSurfaceTransformProps) => ({
            inDayComponent: {
                component: DayGridInDayComponent,
                additionalProps: {
                    viewState,
                },
            },
            inDayContentComponent: {
                component: DayGridInDayContentComponent,
                additionalProps: {
                    viewState,
                    calendarConfig: config,
                },
            },
            allDayComponent: {
                component: DayGridAllDayComponent,
                additionalProps: {
                    viewState,
                    calendarConfig,
                    calendarDayContent: content,
                },
            },
            allDayHeaderComponent: {
                component: DayGridAllDayHeaderComponent,
                additionalProps: {
                    viewState,
                    calendarConfig: config,
                },
            },
        }),
        [viewState]
    );

    if (!viewState) {
        return null;
    }

    const overflowItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: {
            onClick: onOverflowItemClicked,
        },
        itemsDecorator: ({ item, ...divProps }) => <div {...divProps} />,
        template: overflowItemTemplate,
    };

    const templateFunction = (surfaceStore, model, state) => {
        const templateFunc = renderAsAllDay(model) ? allDayItemTemplate : inDayItemTemplate;
        return templateFunc(
            surfaceStore,
            model,
            shouldGrayOutItemForScenario(model) && greyOutUnselectedItems
                ? getLightColorScheme()
                : getCalendarEventColorScheme(model),
            getCharm(model),
            state,
            false,
            false
        );
    };

    const dragHelperDecorator = createDragHelperDecorator(viewState);

    const dragDropInDayItemConfig = {
        itemsDecorator: dragHelperDecorator,
        template: templateFunction,
    };

    const dragDropAllDayItemConfig = {
        itemsDecorator: dragHelperDecorator,
        template: templateFunction,
    };

    const gridItemsDecorator = createItemsDecorator(viewState, () =>
        isFeatureEnabled('cal-surface-itemExpand')
    );
    const config = {
        ...createDayGridConfig(viewState),
        onSurfaceClick: (
            start: OwaDate,
            end: OwaDate,
            isAllDay: boolean,
            isClickAndDragEvent: boolean,
            selectedCalendarFolderId: string
        ) =>
            onSurfaceGridClickHandler({
                start: start,
                end: end,
                isAllDay: isAllDay,
                isClickAndDragEvent: isClickAndDragEvent,
                selectedCalendarFolderId: selectedCalendarFolderId,
            }),
        onSurfaceDoubleClick: (
            start: OwaDate,
            end: OwaDate,
            isAllDay: boolean,
            isClickAndDragEvent: boolean,
            selectedCalendarFolderId: string
        ) =>
            onSurfaceGridDoubleClickHandler({
                start: start,
                end: end,
                isAllDay: isAllDay,
                isClickAndDragEvent: isClickAndDragEvent,
                selectedCalendarFolderId: selectedCalendarFolderId,
            }),
        onSurfaceMouseDown: onGridMouseDown,
        shouldShowCurrentTime: () => true,
        dragDropConfig: {
            canDrag: canDragItem,
            canDrop: canDropItemType,
            onItemDropped: onItemDroppedHandler,
            onDragStart: onDragStart,
            onDragEnd: onDragEnd,
            getSurfaceItemForPreview: getSurfaceItemForPreview,
            colorProvider: getCalendarEventColorScheme,
        },
        getItemConfig: model => {
            if (isOverflowIndicatorId(model.ItemId.Id)) {
                return overflowItemsConfig;
            }
            if (isPreviewItemId(model.ItemId.Id)) {
                return {
                    itemsDecorator: gridItemsDecorator,
                    template: blankItemTemplate,
                };
            }

            if (isDragItemId(model.ItemId.Id)) {
                return renderAsAllDay(model) ? dragDropAllDayItemConfig : dragDropInDayItemConfig;
            }
            return {
                itemsEventHandlers: {
                    onClick: onClickHandler,
                    onDoubleClick: onDoubleClickHandler,
                    onContextMenu: onContextMenuHandler,
                    onKeyDown: onKeydownHandler,
                    onMouseDown: onMouseDownHandler,
                },
                itemsDecorator: ({ item, ...divProps }) => (
                    <div
                        {...divProps}
                        className={classNames(
                            styles.gridItem,
                            'calendar-SelectionStyles-resizeBoxParent',
                            {
                                itemSelected:
                                    highlightedEventId && !shouldGrayOutItemForScenario(item),
                            }
                        )}
                        data-calitemid={item.ItemId.Id}
                    />
                ),
                template: templateFunction,
            };
        },
        freeBusyTemplate: (model: CalendarEvent) =>
            freeBusyTemplate(
                model.ItemId.Id,
                styles.freeBusyColumnShadow,
                getCalendarEventColorScheme(model)
            ),
        disableWorkingHours: !enableWorkingHours,
    };

    return (
        <DayGridContextProvider enableWorkingHours={enableWorkingHours}>
            <DraggableDayGrid
                scenarioId={scenario}
                config={config}
                getCalendarDayContentConfig={getCalendarDayContentConfig}
                maxAllDayRowsToShow={maxAllDayRowsToShow}
                highlightedEventId={highlightedEventId}
                numDaysInDayView={numDaysInDayView}
                shouldSortAllDaySelectionToFront={shouldSortAllDaySelectionToFront}
                shouldHighlightColumnAllDayBusyAway={shouldHighlightColumnAllDayBusyAway}
                perfDatapoint={perfDatapoint}
            />
        </DayGridContextProvider>
    );
});

DayGridView.defaultProps = {
    enableWorkingHours: true,
};

const blankItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    itemState?: any
): JSX.Element => {
    // blank items have calPrimary overwritten by calLighter
    let colorScheme = getCalendarEventColorScheme(model);
    colorScheme = { ...colorScheme, ...{ calPrimary: colorScheme.calLighter } };
    return blankItemGridTemplate(store, model, colorScheme, itemState);
};

export default DayGridView;
